/**
 * Archive 画面: マッチ履歴の一覧と SGF プレイバック。
 *
 * - matchId なし: GET /api/me/matches で自分の終局マッチ一覧を表示。
 * - matchId 指定: GET /api/matches/:id (メタ) + /api/matches/:id/sgf (SGF テキスト) を fetch。
 *   結果バッジ + 対戦相手 + 試合日時 + SGF コードブロック (コピー / DL) を表示。
 */

import { useEffect, useState } from 'react';
import { Loading } from '../components/Loading';
import { StudyButton } from '../components/StudyButton';
import { TitleBar } from '../components/TitleBar';
import { WasmError } from '../components/WasmError';
import { templateLabel } from '../lib/game-meta';
import { mySeatOf, type Snapshot, sideOnBoard, slotSideOnBoard } from '../lib/match-snapshot';
import { navigate, onLinkClick } from '../lib/navigate';
import { useWasmReady } from '../lib/useWasmReady';
import { useSession } from '../state/session';
import { ResultBoardList, ResultBoardNav, ResultHero } from './Result';

interface MatchSummary {
  id: string;
  code: string;
  template: string;
  state: string;
  host_sub: string;
  guest_sub: string | null;
  host_name: string | null;
  guest_name: string | null;
  winner: string | null;
  finish_reason: string | null;
  created_at: number;
  finished_at: number | null;
  has_archive: boolean;
}

export function Archive({ matchId }: { matchId?: string }) {
  if (matchId) return <ArchiveDetail matchId={matchId} />;
  return <ArchiveList />;
}

function ArchiveList() {
  const me = useSession((s) => s.me);
  const [items, setItems] = useState<MatchSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetch('/api/me/matches', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as MatchSummary[];
      })
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : '取得失敗'));
  }, []);

  return (
    <main className="app-shell">
      <TitleBar title="過去のゲーム" back={{ href: '/' }} />
      <section>
        {error && <p className="error">{error}</p>}
        {items == null && !error && <Loading label="読み込み中" />}
        {items && items.length === 0 && (
          <div className="empty-state">
            <span className="empty-state-mark">no matches yet</span>
            <span className="empty-state-text">まだ終局済みのマッチはありません。</span>
          </div>
        )}
        {items && items.length > 0 && (
          <ul className="match-list" data-testid="match-list">
            {items.map((m) => {
              const r = computeResult(m, me?.sub);
              const ts = m.finished_at ?? m.created_at;
              const opponent = me?.sub === m.host_sub
                ? (m.guest_name ?? (m.guest_sub ? null : 'CPU'))
                : m.host_name;
              return (
                <li key={m.id} className="match-row">
                  <a href={`/archive/${m.id}`} onClick={onLinkClick(`/archive/${m.id}`)}>
                    <span className="match-result" data-result={r.kind}>
                      {r.label}
                    </span>
                    <div className="match-meta">
                      <span className="match-template">{templateLabel(m.template)}</span>
                      {opponent && <span className="match-opponent">vs {opponent}</span>}
                    </div>
                    <time>{new Date(ts).toLocaleString()}</time>
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}

function ArchiveDetail({ matchId }: { matchId: string }) {
  const me = useSession((s) => s.me);
  const [meta, setMeta] = useState<MatchSummary | null>(null);
  const [sgf, setSgf] = useState<string | null>(null);
  /** 再生用 snapshot (= MatchState)。 新しいマッチのみ。 古いマッチは null のまま。 */
  const [replay, setReplay] = useState<Snapshot | null>(null);
  /** replay.json が無い (= 古いマッチ)。 true なら SGF テキスト表示にフォールバック。 */
  const [replayMissing, setReplayMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { wasmReady, wasmError, retryWasm } = useWasmReady();
  const [sgfOpen, setSgfOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetch(`/api/matches/${matchId}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as MatchSummary;
      })
      .then((m) => !cancelled && setMeta(m))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : '取得失敗'));
    void fetch(`/api/matches/${matchId}/sgf`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((s) => !cancelled && setSgf(s))
      .catch(() => {
        /* SGF が無い場合もある: meta だけ表示 */
      });
    // 再生用 JSON。 404 (古いマッチ) なら SGF テキストにフォールバック。
    void fetch(`/api/matches/${matchId}/replay`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 404) {
          if (!cancelled) {
            setReplayMissing(true);
            setSgfOpen(true);
          }
          return null;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as Snapshot;
      })
      .then((s) => s && !cancelled && setReplay(s))
      .catch(() => {
        if (!cancelled) {
          setReplayMissing(true);
          setSgfOpen(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [matchId]);

  async function copySgf() {
    if (!sgf) return;
    try {
      await navigator.clipboard.writeText(sgf);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  function downloadSgf() {
    if (!sgf || !meta) return;
    const blob = new Blob([sgf], { type: 'application/x-go-sgf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${meta.code}-${meta.id}.sgf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const r = meta ? computeResult(meta, me?.sub) : null;
  const sgfHref = `/api/matches/${matchId}/sgf`;

  // 自分の役 → MatchSeat。 盤の向き・各盤の勝敗表示に使う。 観戦者 (一覧から他人の
  // マッチを開いた等) は null。
  const myRole: 'host' | 'guest' | 'spectator' =
    meta && me?.sub && me.sub === meta.host_sub
      ? 'host'
      : meta && me?.sub && me.sub === meta.guest_sub
        ? 'guest'
        : 'spectator';
  const mySeat = replay ? mySeatOf(replay, myRole) : null;

  // tally: 自分視点の 勝/負/引 数 (Result と同じロジック)。
  let mine = 0;
  let opp = 0;
  let drawCount = 0;
  if (replay && (myRole === 'host' || myRole === 'guest')) {
    for (const b of replay.boards) {
      const br = b.result;
      if (br === null) continue;
      if (br === 'Draw') { drawCount++; continue; }
      const mySlot = slotSideOnBoard(replay, mySeat, b.board);
      if (!mySlot) continue;
      if (br === mySlot) mine++;
      else opp++;
    }
  }
  const totalBoards = replay?.boards?.length ?? 0;
  const showTally = r && r.kind !== 'unknown' && totalBoards >= 2;

  return (
    <main className="result-shell">
      <TitleBar
        title="アーカイブ"
        back={{ href: '/archive' }}
        menu={[
          ...(sgfHref
            ? [{
                label: 'SGF をダウンロード',
                onClick: () => { window.location.href = sgfHref; },
              }]
            : []),
          { label: 'ホームに戻る', onClick: () => navigate('/') },
        ]}
      />

      {error && <p className="error">{error}</p>}
      {wasmError && (
        <div className="wasm-error-banner">
          <WasmError onRetry={retryWasm} />
        </div>
      )}

      {meta && r && (
        <ResultHero
          resultKind={r.kind}
          resultLabel={r.label}
          tally={showTally ? { mine, opp, drawCount } : null}
          opponentName={me?.sub === meta.host_sub
            ? (meta.guest_name ?? (meta.guest_sub ? null : 'CPU'))
            : meta.host_name}
        />
      )}

      <ResultBoardNav snapshot={replay} mySeat={mySeat} />

      <ResultBoardList
        snapshot={replay}
        mySeat={mySeat}
        sideForBoard={(b) => sideOnBoard(replay, mySeat, b.board)}
        wasmReady={wasmReady}
      />

      {!replay && !replayMissing && <Loading label="再生データを読み込み中" />}

      {meta && (
        <section className="home-actions">
          <StudyButton
            snapshot={replay}
            title={templateLabel(meta.template)}
            outcome={meta.winner}
            seat={mySeat}
            players={[meta.host_name ?? 'ゲスト', meta.guest_name ?? 'CPU']}
          />
        </section>
      )}

      <section>
        <details
          className="archive-sgf-details"
          open={sgfOpen}
          onToggle={(e) => setSgfOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary>棋譜 (SGF テキスト)</summary>
          <div className="archive-sgf-body">
            {sgf && (
              <div className="archive-sgf-actions">
                <button className="btn-link" onClick={copySgf}>
                  {copied ? 'コピー済' : 'コピー'}
                </button>
                <button className="btn-link" onClick={downloadSgf}>
                  ダウンロード
                </button>
              </div>
            )}
            {sgf == null && !error && <p className="loading-row">読み込み中…</p>}
            {sgf && <pre className="sgf-block">{sgf}</pre>}
          </div>
        </details>
      </section>
    </main>
  );
}

function computeResult(
  m: MatchSummary,
  myId?: string,
): { kind: 'win' | 'loss' | 'draw' | 'unknown'; label: string } {
  if (!m.winner) return { kind: 'unknown', label: '中断' };
  // D1 / 送信プロトコル上の winner は "host" / "guest" / "draw" (lowercase)。
  const w = m.winner.toLowerCase();
  if (w === 'draw') return { kind: 'draw', label: '引き分け' };
  if (myId == null) return { kind: 'unknown', label: m.winner };
  const youAreHost = m.host_sub === myId;
  const youAreGuest = m.guest_sub === myId;
  const won = (w === 'host' && youAreHost) || (w === 'guest' && youAreGuest);
  const lost = (w === 'host' && youAreGuest) || (w === 'guest' && youAreHost);
  if (won) return { kind: 'win', label: '勝ち' };
  if (lost) return { kind: 'loss', label: '負け' };
  return { kind: 'unknown', label: m.winner };
}
